import { useDisclosure } from '@mantine/hooks'
import { isNil } from 'lodash'
import { useEffect, useRef } from 'react'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { mapStore } from 'stores/mapStore/mapStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { calculateTextWidth } from 'utils/calculateTextWidth'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { usePrimaryValueGroup } from 'utils/usePrimaryValueGroup'
import { useSetMapOverlayZIndex } from 'utils/useSetMapOverlayZIndex'
import { Label } from '../../Label/Label'
import type {
  IRRIGATION_FLOW_METER_VALUE_META,
  TIrrigationFlowMeterValueTypesToPropsDictionary,
} from '../IrrigationFlowMeterValuesCache'

export type TIrrigationZoneBadgeComponentProps = {
  meta: IRRIGATION_FLOW_METER_VALUE_META
  valueTypesToPropsDictionary: TIrrigationFlowMeterValueTypesToPropsDictionary
}

export const IrrigationFlowMeterBadgeComponent = (props: TIrrigationZoneBadgeComponentProps) => {
  const [isHovered, { close, open }] = useDisclosure(false)
  const primaryValueGroup = usePrimaryValueGroup()

  const { showMapBadgeNames, valueType } = mapControlsStore.useSelector((s) => ({
    showMapBadgeNames: s.showMapBadgeNames,
    valueType: !primaryValueGroup ? null : s.mapVisualValueGroup.POINT[primaryValueGroup]?.valueType,
  }))

  const zoom = mapStore.useSelector((s) => s.zoom)
  const selectedDevice = selectedFieldAssetsStore.useSelector((s) => s.flowMeterDevice)
  const ref = useRef<HTMLDivElement>(null)
  const flowMeterKey = props.meta.lngLat
  const isSelected = flowMeterKey === selectedDevice

  useSetMapOverlayZIndex({ ref, mItem: 'M_InBlockPoint', isSelected, isHighlighted: false, isHovered })

  useEffect(() => {
    close()
  }, [zoom])

  if (isNil(valueType)) return null

  const valueTypeForUse = valueType as string
  const valueTypeForUseData = props.valueTypesToPropsDictionary?.[valueTypeForUse]

  let value = valueTypeForUseData?.children
  let size = 40

  if (value) {
    const nameTextWidth = calculateTextWidth({
      fontSize: '14px',
      fontWeight: 'bold',
      text: value?.toString(),
    })

    size = Math.max(40, nameTextWidth + 10)
  }

  const containerStyles = props?.valueTypesToPropsDictionary?.[valueType]?.getContainerCSS?.()

  return (
    <div
      ref={ref}
      onClick={() => {
        setSelectedFieldAsset({ lngLat: props.meta.lngLat })
      }}
      onMouseEnter={open}
      onMouseLeave={close}
    >
      {(isHovered || showMapBadgeNames) && <Label bottom={48}>{props.meta.name}</Label>}

      <div
        css={{
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'flex',
          alignContent: 'center',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          border: `2px solid ${(isSelected && 'yellow') || 'white'}`,
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          ...containerStyles,
        }}
      >
        <div
          css={{
            zIndex: 1,
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 'bold',
            textAlign: 'center',
          }}
        >
          {value}
        </div>
      </div>
    </div>
  )
}
